import * as React from 'react';
import { InView } from 'react-intersection-observer';
import { useTransition, animated, UseTransitionProps } from 'react-spring';

import {
  Col,
  ProductTilePlaceholder,
  ProductCardPlaceholder,
} from '@notino/react-styleguide';
import {
  GetProductsByIdBatchedQuery,
  VpProduct,
} from '@notino/shared/definitions/types';

import ProductItem from '@components/Universals/ProductItem';
import {
  IProductOuterProps,
  IProductItemProps,
} from '@components/Universals/ProductItem/model';
import { ProductListHolder } from '@components/Universals/ProductListHolder';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';

import { WISHLIST_PAGE_SIZE } from '../constants';
import { useWishlistStore } from '../store';
import { useRegisteredWatchdog } from '../tracking/useRegisteredWatchdog';

import { ClickBlocker, ProductItemWrapper, StyledCheckbox } from './styled';

const columns = { xs: 6, md: 3 };
const animationConfiguration = (enabled: boolean): UseTransitionProps => ({
  from: { opacity: 1, height: '100%' },
  enter: { opacity: 1 },
  leave: { opacity: 0 },
  keys: (item: GetProductsByIdBatchedQuery['vpProductByIds'][number]) =>
    item?.id ?? '',
  config: {
    duration: enabled ? 300 : 0,
    clamp: true,
  },
});

type AnimatedProductListProps = Pick<
  IProductItemProps,
  'canSpaRedirect' | 'showAddToCartModal'
> & {
  products: GetProductsByIdBatchedQuery['vpProductByIds'];
  productItemProps?: IProductOuterProps;
  isLoading?: boolean;
  disableAnimation?: boolean;
  isSharedWishlist?: boolean;
  onProductInView?: (
    product: GetProductsByIdBatchedQuery['vpProductByIds'][number]
  ) => void;
};

const AnimatedProductList = ({
  products,
  productItemProps = {},
  showAddToCartModal,
  canSpaRedirect,
  isLoading,
  disableAnimation,
  onProductInView,
}: AnimatedProductListProps) => {
  const transitions = useTransition(
    products,
    React.useMemo(
      () => animationConfiguration(!disableAnimation),
      [disableAnimation]
    )
  );

  const { registeredEmails } = useRegisteredWatchdog({ products });
  const { release_discovery_product_card_2026 } = useFeatureFlags();
  const selectedIds = useWishlistStore((x) => x.selectedIds);
  const selectionMode = useWishlistStore((x) => x.selectionMode);
  const { setProductSelected } = useWishlistStore((x) => x.actions);

  return (
    <ProductListHolder>
      {transitions((style, product: VpProduct, _, index) => {
        const catalogId = product.catalogId ?? 0;
        const hasRegisteredWatchdog = Boolean(
          registeredEmails &&
            registeredEmails[catalogId] &&
            registeredEmails[catalogId].length > 0
        );

        const isSelected = selectedIds.includes(product.id ?? '');
        const toggleProductSelection = () => {
          setProductSelected(product.id ?? '', !isSelected);
        };

        const handleSelectionKeyDownCapture = (
          event: React.KeyboardEvent<HTMLDivElement>
        ) => {
          if (!selectionMode || (event.key !== ' ' && event.key !== 'Enter')) {
            return;
          }

          const target = event.target as HTMLElement;
          const isCheckboxTarget = Boolean(
            target.closest('input[type="checkbox"], [role="checkbox"]')
          );

          if (!isCheckboxTarget) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          toggleProductSelection();
        };

        const handleSelectionClickOverride = (
          event: React.MouseEvent<HTMLAnchorElement>
        ) => {
          if (!selectionMode) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();

          // Space/Enter can dispatch a synthetic click after keydown; skip it.
          if (event.detail === 0) {
            return;
          }

          toggleProductSelection();
        };

        return (
          <Col key={product.id} {...columns}>
            <animated.div style={style} key={product.id}>
              <InView
                style={{ height: '100%' }}
                threshold={0.5}
                onChange={(inView) => inView && onProductInView?.(product)}
              >
                <ProductItemWrapper
                  selected={isSelected}
                  selectionMode={selectionMode}
                  isNewDesign={release_discovery_product_card_2026}
                  onKeyDownCapture={handleSelectionKeyDownCapture}
                  onClick={() =>
                    selectionMode &&
                    setProductSelected(product.id ?? '', !isSelected)
                  }
                >
                  {selectionMode && <ClickBlocker />}

                  <ProductItem
                    product={product}
                    hasRegisteredWatchdog={hasRegisteredWatchdog}
                    buyButtonDisabled={selectionMode}
                    pictogramsHidden={selectionMode}
                    showWishlistButton={!selectionMode}
                    selectable={
                      release_discovery_product_card_2026
                        ? selectionMode
                        : undefined
                    }
                    selected={
                      release_discovery_product_card_2026
                        ? isSelected
                        : undefined
                    }
                    position={index + 1}
                    showAddToCartModal={showAddToCartModal}
                    canSpaRedirect={!selectionMode && canSpaRedirect}
                    onClickOveride={
                      selectionMode ? handleSelectionClickOverride : undefined
                    }
                    useMasterUrl={false}
                    iconsSubstitution={
                      selectionMode && !release_discovery_product_card_2026 ? (
                        <StyledCheckbox checked={isSelected} />
                      ) : undefined
                    }
                    {...productItemProps}
                  />
                </ProductItemWrapper>
              </InView>
            </animated.div>
          </Col>
        );
      })}

      {isLoading &&
        new Array(WISHLIST_PAGE_SIZE).fill(0).map((_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Col key={index} {...columns}>
            {release_discovery_product_card_2026 ? (
              <ProductCardPlaceholder />
            ) : (
              <ProductTilePlaceholder />
            )}
          </Col>
        ))}
    </ProductListHolder>
  );
};

export default AnimatedProductList;
